import { useState } from 'react'
import { CompositeNavigationProp } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { useSignUp } from '@hooks'
import { AuthStackParamList } from '@navigation'
import { useLanguage } from '@context'
import { useAuthCredentialsStore, useLanguageStore } from '@store'
import { isUncodedBadRequest, isValidEmail, normalizeEmail, translateApiError } from '@utils'

interface FieldErrors {
  email?: boolean
  password?: boolean
  confirmPassword?: boolean
}

interface useRegisterResult {
  email: string
  password: string
  confirmPassword: string
  isFilled: boolean
  isPending: boolean
  error: string
  fieldErrors: FieldErrors
  isPasswordVisible: boolean
  isConfirmPasswordVisible: boolean
  setEmail: React.Dispatch<React.SetStateAction<string>>
  setPassword: React.Dispatch<React.SetStateAction<string>>
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>
  togglePasswordVisibility: () => void
  toggleConfirmPasswordVisibility: () => void
  handleSignUp: () => void
}

export const useRegister = (
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<AuthStackParamList, 'Register'>,
    NativeStackNavigationProp<AuthStackParamList, keyof AuthStackParamList>
  >
): useRegisterResult => {
  const { t, currentLanguage } = useLanguage()
  const { mutate: signUpMutation, isPending } = useSignUp()
  const { setAuthCredentials } = useAuthCredentialsStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false)

  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const isFilled = Boolean(email && password && password === confirmPassword)

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible)
  }

  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
  }

  const handleSignUp = () => {
    setError('')
    // Same email normalisation as login (trailing space / caps break @IsEmail).
    const cleanedEmail = normalizeEmail(email)
    const nextFieldErrors: FieldErrors = {
      email: !cleanedEmail,
      password: !password,
      confirmPassword: !confirmPassword,
    }

    if (!cleanedEmail || !password || !confirmPassword) {
      setFieldErrors(nextFieldErrors)
      setError(t('register.error.noInfo'))
      return
    }

    if (!isValidEmail(cleanedEmail)) {
      setFieldErrors({ email: true })
      setError(t('errors.invalidEmail'))
      return
    }

    if (password !== confirmPassword) {
      setFieldErrors({ password: true, confirmPassword: true })
      setError(t('register.error.notSamePassword'))
      return
    }

    setFieldErrors({})

    signUpMutation(
      { email: cleanedEmail, password },
      {
        onSuccess: () => {
          setAuthCredentials(cleanedEmail)
          // Keep this device in the language of the sign-up: a server that
          // does not store it on the new profile yet would answer French,
          // and the app would follow it.
          useLanguageStore.getState().switchLanguage(currentLanguage)
          navigation.navigate('RegistrationSteps')
        },
        onError: (err) => {
          // The only field the API validates here is the e-mail.
          if (isUncodedBadRequest(err)) {
            setFieldErrors({ email: true })
            setError(t('errors.invalidEmail'))
            return
          }
          const { message } = translateApiError(t, err)
          const apiCode = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
          if (
            apiCode === 'Identity.SignUp.EmailAlreadyTaken' ||
            apiCode === 'IdentityAndAccess.EmailAlreadyTakenError'
          ) {
            setFieldErrors({ email: true })
          }
          setError(message)
        },
      },
    )
  }

  return {
    email,
    password,
    confirmPassword,
    isFilled,
    isPending,
    error,
    fieldErrors,
    isPasswordVisible,
    isConfirmPasswordVisible,
    setEmail,
    setPassword,
    setConfirmPassword,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleSignUp,
  }
}
